// Assignments → Notifications (owner, 2026-10-07 — docs/decisions.md D-306, D-310;
// Rules → Upload Notifications until D-310): the table's rows and the warning
// above it, as data.
//
// The owner: "a company representative (the AP/AR, to be specific) plus two
// controllers are always receiving notifications ... We could also add a
// warning block at the top of the page if any company does not have the 1 + 2
// required representatives." The floor is `UPLOAD_NOTIFICATION_MINIMUM` from
// `@ap/shared`; it is a warning, never a cap and never a refusal.
//
// TWO NOTIFICATIONS SINCE D-336 (owner, 2026-10-09): every company has a row for New
// Invoices Upload and a row for Invoice Passed Without Any Approvals. The first
// keeps three lists, AP/AR, Controllers and Others. The second keeps ONE list,
// headed Assigned To like the table's column, whose people carry no kind
// ("There is not a specific structure to who should be receiving this one.");
// it asks for at least one person on it — "Require at least one person for the
// new alert, though we do not need to recommend roles" — and the same warning
// names a company short of it.

import {
  NOTIFICATION_RECIPIENT_KINDS,
  NOTIFICATION_TYPE_LABEL,
  NOTIFICATION_TYPES,
  type ListedNotificationType,
  type NotificationType,
  UPLOAD_NOTIFICATION_KIND_LABEL,
  UPLOAD_NOTIFICATION_MINIMUM,
  type UploadNotificationKind,
  isSingleListNotification,
} from '@ap/shared'
import { countOf, sentenceList } from './format'
import type { Company, UploadNotificationRecipient } from './types'

/** The heading of the one list a notification with no kinds keeps — the table's column name. */
const SINGLE_LIST_LABEL = 'Assigned To'

/** One list on a row, in the order the editor shows them. */
interface RecipientList {
  /** The list's kind; null for the one list of a notification that keeps one. */
  kind: UploadNotificationKind | null
  /** Its heading: AP/AR, Controllers or Others, or Assigned To for a single list. */
  label: string
  recipients: UploadNotificationRecipient[]
}

/** One table row: a notification, a company, and who is listed for it, list by list. */
export interface UploadNotificationRow {
  /** The Notification column (D-310); its label is `NOTIFICATION_TYPE_LABEL`'s. */
  notification_type: ListedNotificationType
  company_id: string
  company_name: string
  /** Three for New Upload, one for Invoice Passed Without Any Approvals. */
  lists: RecipientList[]
}

/** The lists a notification keeps, empty. */
export function emptyLists(notificationType: ListedNotificationType): RecipientList[] {
  if (isSingleListNotification(notificationType)) return [{ kind: null, label: SINGLE_LIST_LABEL, recipients: [] }]
  return NOTIFICATION_RECIPIENT_KINDS[notificationType].map((kind) => ({
    kind,
    label: UPLOAD_NOTIFICATION_KIND_LABEL[kind],
    recipients: [],
  }))
}

/** The row's list of that kind (null: its single list), or undefined when it keeps no such list. */
export function listOf(row: UploadNotificationRow, kind: UploadNotificationKind | null): RecipientList | undefined {
  return row.lists.find((list) => list.kind === kind)
}

const keyOf = (notificationType: NotificationType, companyId: string) => `${notificationType}:${companyId}`

/**
 * One row per notification per ACTIVE company — by company name, each company's
 * notifications in `NOTIFICATION_TYPES` order — with its ACTIVE recipients sorted by name. A
 * recipient of an Inactive company is not shown: no invoice is routed to one.
 */
export function uploadNotificationRows(
  companies: readonly Company[],
  recipients: readonly UploadNotificationRecipient[],
): UploadNotificationRow[] {
  const rows = new Map<string, UploadNotificationRow>()
  for (const company of [...companies]
    .filter((c) => c.is_active)
    .sort((a, b) => a.company_name.localeCompare(b.company_name))) {
    for (const notificationType of NOTIFICATION_TYPES) {
      rows.set(keyOf(notificationType, company.company_id), {
        notification_type: notificationType,
        company_id: company.company_id,
        company_name: company.company_name,
        lists: emptyLists(notificationType),
      })
    }
  }
  for (const recipient of recipients) {
    if (!recipient.is_active) continue
    const row = rows.get(keyOf(recipient.notification_type, recipient.company_id))
    if (row !== undefined) listOf(row, recipient.kind ?? null)?.recipients.push(recipient)
  }
  for (const row of rows.values()) {
    for (const list of row.lists) {
      list.recipients.sort((a, b) => (a.user_name ?? a.user_email).localeCompare(b.user_name ?? b.user_email))
    }
  }
  return [...rows.values()]
}

/**
 * True when the row is short of its notification's floor: for New Upload,
 * fewer than one AP/AR or fewer than two controllers; for Invoice Passed
 * Without Any Approvals, nobody on its one list (D-336).
 */
export function isShortOfRecipients(row: UploadNotificationRow): boolean {
  return row.lists.some((list) =>
    list.kind === null ? list.recipients.length === 0 : list.recipients.length < UPLOAD_NOTIFICATION_MINIMUM[list.kind],
  )
}

/** The rows the warning names, in table order. */
export function companiesShortOfRecipients(rows: readonly UploadNotificationRow[]): UploadNotificationRow[] {
  return rows.filter(isShortOfRecipients)
}

/**
 * The warning's sentences, one per notification that has a company short of its
 * floor, each naming the notification and each company exactly, joined with "and" and
 * the Oxford comma. Empty when none is short. The owner's wording
 * (2026-10-07): "2 companies are missing required assignments: SeniorLIFE York
 * and Seniors at Home." Since D-336 each sentence names its notification, because a
 * company can be short of one and not the other: "2 companies are missing
 * required assignments for New Upload: SeniorLIFE York and Seniors at
 * Home."
 */
export function shortOfRecipientsSentence(rows: readonly UploadNotificationRow[]): string {
  const short = companiesShortOfRecipients(rows)
  return NOTIFICATION_TYPES.flatMap((notificationType) => {
    const ofType = short.filter((row) => row.notification_type === notificationType)
    if (ofType.length === 0) return []
    const names = sentenceList(ofType.map((row) => row.company_name))
    return [
      `${countOf(ofType.length, 'company')} ${ofType.length === 1 ? 'is' : 'are'} missing required assignments for ${NOTIFICATION_TYPE_LABEL[notificationType]}: ${names}.`,
    ]
  }).join(' ')
}

/**
 * The Assigned To column (owner, 2026-10-07): everyone listed on the row,
 * whatever their list, by name. A person on two lists appears once. New
 * Invoices Upload's three lists stay in the row's editor and in the warning's
 * rule.
 */
export function assignedTo(row: UploadNotificationRow): UploadNotificationRecipient[] {
  const byEmail = new Map<string, UploadNotificationRecipient>()
  for (const list of row.lists) {
    for (const r of list.recipients) {
      const key = r.user_email.toLowerCase()
      if (!byEmail.has(key)) byEmail.set(key, r)
    }
  }
  return [...byEmail.values()].sort((a, b) =>
    (a.user_name ?? a.user_email).localeCompare(b.user_name ?? b.user_email),
  )
}

/**
 * What a person on these rows hears about, for the sentence under a bulk
 * picker and in its confirmation: "about new uploads", "about invoices that pass
 * without any approvals", or, for a selection holding both notifications, "about the
 * selected notifications".
 */
export function notificationsPhrase(rows: readonly UploadNotificationRow[]): string {
  const types = new Set(rows.map((row) => row.notification_type))
  if (types.size !== 1) return 'about the selected notifications'
  return isSingleListNotification([...types][0]!) ? 'about invoices that pass without any approvals' : 'about new uploads'
}
